import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Trash2, Send, Plus, Upload, Shuffle } from "lucide-react";
import {
  listSmtpAccounts,
  createSmtpAccount,
  deleteSmtpAccount,
  sendSmtpMail,
  listSmtpSendLog,
  listCampaignsForSmtp,
  bulkCreateSmtpAccounts,
  testSmtpAccount,
  setSmtpAccountActive,
} from "@/lib/smtp-accounts.functions";

export const Route = createFileRoute("/_authenticated/smtp-send")({
  component: SmtpSendPage,
  head: () => ({ meta: [{ title: "SMTP-Versand" }] }),
});

function SmtpSendPage() {
  const qc = useQueryClient();
  const list = useServerFn(listSmtpAccounts);
  const create = useServerFn(createSmtpAccount);
  const del = useServerFn(deleteSmtpAccount);
  const send = useServerFn(sendSmtpMail);
  const logFn = useServerFn(listSmtpSendLog);
  const campaignsFn = useServerFn(listCampaignsForSmtp);
  const bulkCreate = useServerFn(bulkCreateSmtpAccounts);
  const testAccount = useServerFn(testSmtpAccount);
  const setActive = useServerFn(setSmtpAccountActive);

  const activeMut = useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) => setActive({ data: v }),
    onSuccess: (_d, v) => {
      toast.success(v.is_active ? "Account aktiviert" : "Account pausiert");
      qc.invalidateQueries({ queryKey: ["smtp-accounts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const accounts = useQuery({
    queryKey: ["smtp-accounts"],
    queryFn: () => list(),
  });
  const log = useQuery({
    queryKey: ["smtp-send-log"],
    queryFn: () => logFn(),
    refetchInterval: 8000,
  });
  const campaigns = useQuery({
    queryKey: ["smtp-campaigns"],
    queryFn: () => campaignsFn(),
  });

  // Neues Konto
  const [label, setLabel] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState(465);
  const [tls, setTls] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const createMut = useMutation({
    mutationFn: () =>
      create({
        data: {
          label,
          smtp_host: host,
          smtp_port: port,
          smtp_tls: tls,
          smtp_username: username,
          password,
          from_email: fromEmail,
          from_name: fromName || null,
          reply_to: replyTo || null,
          is_default: isDefault,
        },
      }),
    onSuccess: () => {
      toast.success("SMTP-Konto gespeichert");
      setLabel("");
      setHost("");
      setPort(465);
      setTls(true);
      setUsername("");
      setPassword("");
      setFromEmail("");
      setFromName("");
      setReplyTo("");
      setIsDefault(false);
      qc.invalidateQueries({ queryKey: ["smtp-accounts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Konto gelöscht");
      qc.invalidateQueries({ queryKey: ["smtp-accounts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ----- Bulk CSV-Upload + Test --------------------------------------------
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvReport, setCsvReport] = useState<string[]>([]);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; msg: string }>>({});

  function parseCsv(text: string): Array<Record<string, string>> {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length < 2) return [];
    const splitLine = (line: string): string[] => {
      const out: string[] = [];
      let cur = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          if (inQuotes && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (c === "," && !inQuotes) {
          out.push(cur);
          cur = "";
        } else {
          cur += c;
        }
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
    return lines.slice(1).map((line) => {
      const cells = splitLine(line);
      const row: Record<string, string> = {};
      headers.forEach((h, i) => (row[h] = cells[i] ?? ""));
      return row;
    });
  }

  async function handleAccountsCsvUpload(file: File) {
    setCsvBusy(true);
    setCsvReport([]);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        toast.error("CSV ist leer oder ungültig.");
        return;
      }
      const items = rows
        .map((r) => {
          const email = (r.email || r.from_email || r.smtp_username || "").toLowerCase();
          const firstName = r.first_name || r.firstname || "";
          const lastName = r.last_name || r.lastname || "";
          const fullName = `${firstName} ${lastName}`.trim();
          const port = Number(r.smtp_port || r.port || 465);
          return {
            label: fullName ? `${fullName} (${email})` : email,
            smtp_host: r.smtp_host || r.host || "",
            smtp_port: port,
            smtp_tls: port === 465 || port === 587,
            smtp_username: r.smtp_username || email,
            password: r.password || r.smtp_password || "",
            from_email: email,
            from_name: fullName || null,
            reply_to: r.reply_to || null,
          };
        })
        .filter((i) => i.from_email && i.smtp_host && i.password);

      if (!items.length) {
        toast.error("Keine vollständigen Zeilen gefunden (email, smtp_host, password nötig).");
        return;
      }

      const res = await bulkCreate({ data: { items } });
      const report: string[] = [];
      report.push(`Import: ${res.created} neu, ${res.skipped} übersprungen, ${res.failed} Fehler.`);
      for (const r of res.results) {
        if (!r.ok) report.push(`✗ ${r.email}: ${r.error}`);
      }
      toast.success(
        `Import abgeschlossen: ${res.created} neu, ${res.skipped} übersprungen, ${res.failed} Fehler.`,
      );
      qc.invalidateQueries({ queryKey: ["smtp-accounts"] });

      // Automatischer Test jedes neu angelegten Accounts
      const newOnes = res.results.filter((r) => r.ok && !r.skipped && r.id);
      if (newOnes.length) {
        report.push("");
        report.push(`Teste ${newOnes.length} Accounts…`);
        setCsvReport([...report]);
        let ok = 0;
        let fail = 0;
        for (const r of newOnes) {
          try {
            const t = await testAccount({ data: { id: r.id! } });
            if (t.ok) {
              ok++;
              report.push(`✓ ${r.email}: Login + Versand OK`);
              setTestResults((prev) => ({
                ...prev,
                [r.id!]: { ok: true, msg: "OK" },
              }));
            } else {
              fail++;
              const msg = t.error ?? "Test fehlgeschlagen";
              report.push(`✗ ${r.email}: ${msg}`);
              setTestResults((prev) => ({
                ...prev,
                [r.id!]: { ok: false, msg },
              }));
            }
          } catch (e) {
            fail++;
            const msg = e instanceof Error ? e.message : String(e);
            report.push(`✗ ${r.email}: ${msg}`);
            setTestResults((prev) => ({
              ...prev,
              [r.id!]: { ok: false, msg },
            }));
          }
          setCsvReport([...report]);
        }
        toast.success(`Test fertig: ${ok} OK, ${fail} fehlgeschlagen.`);
      }
      setCsvReport([...report]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setCsvBusy(false);
    }
  }

  async function runTest(id: string) {
    setTestingId(id);
    try {
      const t = await testAccount({ data: { id } });
      if (t.ok) {
        toast.success("Test OK – Login funktioniert und Mail wurde abgegeben.");
        setTestResults((prev) => ({ ...prev, [id]: { ok: true, msg: "OK" } }));
      } else {
        const msg = t.error ?? "Test fehlgeschlagen";
        toast.error(msg);
        setTestResults((prev) => ({ ...prev, [id]: { ok: false, msg } }));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
      setTestResults((prev) => ({ ...prev, [id]: { ok: false, msg } }));
    } finally {
      setTestingId(null);
    }
  }

  // Senden
  const [sendAccountId, setSendAccountId] = useState<string>("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sendCampaignId, setSendCampaignId] = useState<string>("");

  const sendMut = useMutation({
    mutationFn: () =>
      send({
        data: {
          account_id: sendAccountId,
          to,
          subject,
          html,
          campaign_id: sendCampaignId || null,
        },
      }),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? "Versand fehlgeschlagen");
        qc.invalidateQueries({ queryKey: ["smtp-send-log"] });
        return;
      }
      toast.success("Mail gesendet");
      setTo("");
      setSubject("");
      setHtml("");
      qc.invalidateQueries({ queryKey: ["smtp-send-log"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Betreff-Rotation + Bulk-Versand
  const [subjectsText, setSubjectsText] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [bulkHtml, setBulkHtml] = useState("");
  const [bulkAccountId, setBulkAccountId] = useState<string>("");
  const [bulkCampaignId, setBulkCampaignId] = useState<string>("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
    ok: number;
    failed: number;
  }>({ done: 0, total: 0, ok: 0, failed: 0 });

  function parseCsvLines(text: string): string[] {
    return (
      text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        // CSV: nimm die erste Spalte falls Kommas vorhanden
        .map((l) => {
          const first = l.split(",")[0];
          return first.replace(/^"|"$/g, "").trim();
        })
        .filter(Boolean)
    );
  }

  async function handleCsvUpload(file: File, target: "subjects" | "recipients") {
    const text = await file.text();
    const lines = parseCsvLines(text);
    if (target === "subjects") {
      setSubjectsText((prev) => (prev ? prev + "\n" : "") + lines.join("\n"));
      toast.success(`${lines.length} Betreffe geladen`);
    } else {
      setRecipientsText((prev) => (prev ? prev + "\n" : "") + lines.join("\n"));
      toast.success(`${lines.length} Empfänger geladen`);
    }
  }

  async function runBulk() {
    const subjects = parseCsvLines(subjectsText);
    const recipients = parseCsvLines(recipientsText);
    if (!bulkAccountId) return toast.error("Bitte SMTP-Konto wählen");
    if (!subjects.length) return toast.error("Mindestens 1 Betreff erforderlich");
    if (!recipients.length) return toast.error("Mindestens 1 Empfänger erforderlich");
    if (!bulkHtml) return toast.error("HTML-Inhalt fehlt");

    setBulkRunning(true);
    setBulkProgress({ done: 0, total: recipients.length, ok: 0, failed: 0 });
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < recipients.length; i++) {
      const rcpt = recipients[i];
      const subj = subjects[i % subjects.length];
      try {
        const result = await send({
          data: {
            account_id: bulkAccountId,
            to: rcpt,
            subject: subj,
            html: bulkHtml,
            campaign_id: bulkCampaignId || null,
          },
        });
        if (!result.ok) throw new Error(result.error ?? "Versand fehlgeschlagen");
        ok++;
      } catch (e) {
        failed++;
        console.error("send failed", rcpt, e);
      }
      setBulkProgress({ done: i + 1, total: recipients.length, ok, failed });
    }
    setBulkRunning(false);
    qc.invalidateQueries({ queryKey: ["smtp-send-log"] });
    toast.success(`Fertig: ${ok} gesendet, ${failed} fehlgeschlagen`);
  }

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="SMTP-Versand"
        description="Eigene SMTP-Zugangsdaten hinterlegen und Mails direkt aus dem Dashboard versenden. Der Versand läuft über eine Edge Function (echtes SMTP via TCP/TLS)."
      />

      <HelpPanel
        title="Hilfe: Mail direkt von hier versenden"
        what={
          <>
            Auf dieser Seite kannst du eine Mail <b>direkt aus dem Dashboard</b> verschicken — ohne
            Plusvibe oder Mailchimp. Du brauchst dafür die Zugangsdaten deines Mail-Anbieters
            (SMTP-Server, Benutzername, Passwort).
          </>
        }
        why={
          <>
            Praktisch für Test-Mails und kleine Sendungen. Für tausende Empfänger sind
            spezialisierte Tools besser, weil sie das Tempo automatisch drosseln.
          </>
        }
        how={[
          <>
            <b>SMTP-Konto eintragen</b>: Server (z.B. <code>smtp.gmail.com</code>), Port (meist
            587), E-Mail und Passwort. Bei Gmail brauchst du ein <b>App-Passwort</b>, nicht dein
            normales.
          </>,
          <>
            <b>Test-Verbindung</b> klicken — grünes Häkchen = passt.
          </>,
          <>
            <b>Empfänger, Betreff, Nachricht</b> eintragen.
          </>,
          <>
            <b>Senden</b> — Status (zugestellt / Fehler) erscheint sofort.
          </>,
        ]}
        tips={[
          <>Passwort wird verschlüsselt gespeichert und niemals im Klartext angezeigt.</>,
          <>
            Gmail: max. 500 Mails/Tag. Outlook: ähnlich. Mehr → eigener SMTP-Anbieter (z.B. Brevo,
            Mailjet).
          </>,
          <>
            Wenn "Connection refused" → Port stimmt nicht oder Firewall blockt. 587 oder 465
            probieren.
          </>,
          <>Erste Mail immer an dich selbst, dann an alle.</>,
        ]}
        glossary={[
          {
            term: "SMTP",
            def: "Die Technik, mit der Mails verschickt werden. Wie der Postbote zwischen deinem Computer und dem Mail-Server.",
          },
          {
            term: "Port",
            def: "Eine Nummer, an der dein Mail-Server lauscht. 587 = normal, 465 = mit SSL, 25 = oft blockiert.",
          },
          {
            term: "App-Passwort",
            def: "Spezielles Passwort, das Gmail/Outlook für Programme ausstellt. Sicherer als dein echtes Login.",
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="size-4" /> SMTP-Accounts als CSV importieren
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            CSV mit Spalten:{" "}
            <code className="text-xs">
              first_name,last_name,email,password,imap_host,imap_port,smtp_host,smtp_port
            </code>
            . Die <code className="text-xs">imap_*</code>-Spalten werden ignoriert. TLS wird bei
            Port 465/587 automatisch aktiviert. Jeder neue Account wird sofort getestet (echte
            Login-Verbindung + Versand an die eigene Adresse).
          </p>
          <Input
            type="file"
            accept=".csv,text/csv"
            disabled={csvBusy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleAccountsCsvUpload(f);
              e.target.value = "";
            }}
          />
          {csvBusy && (
            <p className="text-sm text-muted-foreground">Importiere und teste Accounts…</p>
          )}
          {csvReport.length > 0 && (
            <pre className="text-xs bg-muted/50 rounded-md p-3 max-h-72 overflow-auto whitespace-pre-wrap">
              {csvReport.join("\n")}
            </pre>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="size-4" /> Neues SMTP-Konto
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3 bg-muted/30">
            <span className="text-xs text-muted-foreground mr-1">Schnell-Voreinstellung:</span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setLabel("MailerSend Beratung");
                setHost("smtp.mailersend.net");
                setPort(587);
                setTls(true);
                setUsername("MS_8krIE7@beratungpunkt.de");
                setFromEmail("info@beratungpunkt.de");
                setFromName("Beratungpunkt");
                toast.info("Vorgefüllt – bitte nur noch Passwort eintragen und speichern.");
              }}
            >
              MailerSend · beratungpunkt.de
            </Button>
            <span className="text-xs text-muted-foreground">
              Füllt Host/Port/TLS/User/From – Passwort tippst du selbst.
            </span>
          </div>
          <div className="space-y-2">
            <Label>Bezeichnung</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z. B. Kanzlei Rechtspartnerschaft"
            />
          </div>
          <div className="space-y-2">
            <Label>Absender (From)</Label>
            <Input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="info@kanzleirechtspartnerschaft.de"
            />
          </div>
          <div className="space-y-2">
            <Label>Anzeigename (optional)</Label>
            <Input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Kanzlei Rechtspartnerschaft"
            />
          </div>
          <div className="space-y-2">
            <Label>Reply-To (optional)</Label>
            <Input
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="kontakt@…"
            />
          </div>
          <div className="space-y-2">
            <Label>SMTP Host</Label>
            <Input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="mail.kanzleirechtspartnerschaft.de"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Port</Label>
              <Input type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} />
            </div>
            <div className="space-y-2">
              <Label>SSL/TLS</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch checked={tls} onCheckedChange={setTls} />
                <span className="text-xs text-muted-foreground">
                  {port === 465 ? "Implicit TLS" : port === 587 ? "STARTTLS" : ""}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>SMTP Username</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="mailuser"
            />
          </div>
          <div className="space-y-2">
            <Label>SMTP Passwort</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={isDefault} onCheckedChange={setIsDefault} />
              <Label className="text-sm">Als Standard-Konto setzen</Label>
            </div>
            <Button
              onClick={() => createMut.mutate()}
              disabled={
                !label || !host || !username || !password || !fromEmail || createMut.isPending
              }
            >
              {createMut.isPending ? "Speichere…" : "Konto speichern"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gespeicherte Konten</CardTitle>
        </CardHeader>
        <CardContent>
          {!accounts.data?.accounts?.length ? (
            <p className="text-sm text-muted-foreground">Noch keine Konten hinterlegt.</p>
          ) : (
            <div className="space-y-2">
              {accounts.data.accounts.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between border rounded-md px-3 py-2"
                >
                  <div className="text-sm">
                    <div className="font-medium flex items-center gap-2">
                      {a.label}
                      {a.is_default && <Badge variant="secondary">Standard</Badge>}
                      {!a.is_active && <Badge variant="outline">Pausiert</Badge>}
                      {testResults[a.id] && (
                        <Badge variant={testResults[a.id].ok ? "default" : "destructive"}>
                          {testResults[a.id].ok ? "Test OK" : "Test fehlgeschlagen"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.from_email} · {a.smtp_host}:{a.smtp_port}
                      {a.smtp_tls ? " · TLS" : ""}
                    </div>
                    {testResults[a.id] && !testResults[a.id].ok && (
                      <div className="text-xs text-destructive mt-1">{testResults[a.id].msg}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex items-center gap-2 pr-2 border-r mr-1">
                      <Switch
                        checked={a.is_active}
                        onCheckedChange={(v) => activeMut.mutate({ id: a.id, is_active: v })}
                        disabled={activeMut.isPending}
                      />
                      <span className="text-xs text-muted-foreground">Aktiv</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runTest(a.id)}
                      disabled={testingId === a.id}
                    >
                      {testingId === a.id ? "Teste…" : "Test"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteMut.mutate(a.id)}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="size-4" /> Mail senden
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Konto</Label>
              <Select value={sendAccountId} onValueChange={setSendAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="SMTP-Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.data?.accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label} ({a.from_email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Empfänger</Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="empfaenger@example.com"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Kampagne (optional, für Open-/Bounce-Tracking)</Label>
            <Select
              value={sendCampaignId || "__none__"}
              onValueChange={(v) => setSendCampaignId(v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Keine Kampagne (ohne Tracking)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Keine Kampagne</SelectItem>
                {campaigns.data?.campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Mit Kampagne: Tracking-Pixel wird in HTML eingefügt und Empfänger im Recipient-Log
              gespeichert. Wenn ein Bounce-Inbox-Setup (VERP) konfiguriert ist, wird der Return-Path
              automatisch gesetzt, damit Bounces zugeordnet werden.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Betreff</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>HTML-Inhalt</Label>
            <Textarea
              rows={10}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              placeholder="<p>Hallo …</p>"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => sendMut.mutate()}
              disabled={!sendAccountId || !to || !subject || !html || sendMut.isPending}
            >
              {sendMut.isPending ? "Sende…" : "Senden"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shuffle className="size-4" /> Bulk-Versand mit Betreff-Rotation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP-Konto</Label>
              <Select value={bulkAccountId} onValueChange={setBulkAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="SMTP-Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.data?.accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label} ({a.from_email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fortschritt</Label>
              <div className="text-sm text-muted-foreground h-9 flex items-center">
                {bulkProgress.total
                  ? `${bulkProgress.done}/${bulkProgress.total} · ok ${bulkProgress.ok} · fehler ${bulkProgress.failed}`
                  : "Bereit"}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Kampagne (optional, für Open-/Bounce-Tracking)</Label>
            <Select
              value={bulkCampaignId || "__none__"}
              onValueChange={(v) => setBulkCampaignId(v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Keine Kampagne (ohne Tracking)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Keine Kampagne</SelectItem>
                {campaigns.data?.campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Betreffe (1 pro Zeile, rotiert)</Label>
                <label className="text-xs inline-flex items-center gap-1 cursor-pointer text-primary">
                  <Upload className="size-3" /> CSV
                  <input
                    type="file"
                    accept=".csv,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleCsvUpload(f, "subjects");
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              <Textarea
                rows={8}
                value={subjectsText}
                onChange={(e) => setSubjectsText(e.target.value)}
                placeholder={"Angebot für Sie\nKurze Frage\nUpdate für Ihr Konto"}
              />
              <p className="text-xs text-muted-foreground">
                {parseCsvLines(subjectsText).length} Betreffe geladen
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Empfänger (1 E-Mail pro Zeile)</Label>
                <label className="text-xs inline-flex items-center gap-1 cursor-pointer text-primary">
                  <Upload className="size-3" /> CSV
                  <input
                    type="file"
                    accept=".csv,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleCsvUpload(f, "recipients");
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              <Textarea
                rows={8}
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder={"alice@example.com\nbob@example.com"}
              />
              <p className="text-xs text-muted-foreground">
                {parseCsvLines(recipientsText).length} Empfänger geladen
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>HTML-Inhalt</Label>
            <Textarea
              rows={8}
              value={bulkHtml}
              onChange={(e) => setBulkHtml(e.target.value)}
              placeholder="<p>Hallo …</p>"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={runBulk} disabled={bulkRunning}>
              {bulkRunning
                ? `Sende… (${bulkProgress.done}/${bulkProgress.total})`
                : "Bulk-Versand starten"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Versand-Log (letzte 50)</CardTitle>
        </CardHeader>
        <CardContent>
          {!log.data?.rows?.length ? (
            <p className="text-sm text-muted-foreground">Noch keine Sendeversuche.</p>
          ) : (
            <div className="space-y-1 text-sm">
              {log.data.rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b py-1.5">
                  <div className="min-w-0">
                    <div className="truncate">
                      <span className="font-mono text-xs">{r.to_email}</span> · {r.subject ?? ""}
                    </div>
                    <div className="text-xs text-muted-foreground truncate font-mono">
                      {r.message_id ?? "—"}
                      {r.provider_host ? ` · ${r.provider_host}` : ""}
                      {r.smtp_code ? ` · code ${r.smtp_code}` : ""}
                    </div>
                    {r.smtp_response && r.status === "sent" && (
                      <div className="text-xs text-muted-foreground truncate">
                        {r.smtp_response}
                      </div>
                    )}
                    {r.error && <div className="text-xs text-destructive truncate">{r.error}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={r.status === "sent" ? "default" : "destructive"}>
                      {r.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.sent_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
